import { useParams, useNavigate } from "react-router-dom";
import {
  Star,
  ShoppingCart,
  ArrowLeft,
  Check,
  Shield,
  Truck,
  RefreshCw,
} from "lucide-react";
import { DUMMY_PRODUCTS, useApp } from "@/context/AppContext";
import { AppButton } from "@/components/app/AppButton";
import { ROUTES } from "@/lib/routes";
import { useState } from "react";

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useApp();
  const [addedToCart, setAddedToCart] = useState(false);

  const product = DUMMY_PRODUCTS.find((p) => p.id === id);

  if (!product) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground text-lg">Product not found.</p>
        <AppButton onClick={() => navigate(ROUTES.PRODUCTS)}>
          Back to Products
        </AppButton>
      </main>
    );
  }

  const handleAddToCart = () => {
    addToCart(product);
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const handleBuyNow = () => {
    addToCart(product);
    navigate(ROUTES.CART);
  };

  const discount = Math.round(
    ((product.originalPrice - product.price) / product.originalPrice) * 100,
  );

  return (
    <main className="min-h-screen py-8 md:py-14">
      <div className="wrapper">
        {/* Back Button */}
        <button
          onClick={() => navigate(ROUTES.PRODUCTS)}
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Products
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 xl:gap-16">
          {/* Product Image */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden bg-muted aspect-square">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>
            {/* Badges */}
            <div className="absolute top-4 left-4 flex flex-col gap-2">
              <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">
                {product.category}
              </span>
              <span className="bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                {discount}% OFF
              </span>
            </div>
          </div>

          {/* Product Info */}
          <div className="flex flex-col">
            <div className="mb-2">
              <span className="text-xs font-medium text-primary uppercase tracking-widest">
                {product.material}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              {product.name}
            </h1>

            {/* Rating */}
            <div className="flex items-center gap-1.5 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className="h-4 w-4 fill-yellow-400 text-yellow-400"
                />
              ))}
              <span className="text-sm text-muted-foreground ml-1">
                4.9 (48 reviews)
              </span>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-5">
              <span className="text-3xl font-bold text-foreground">
                ₹{product.price.toLocaleString("en-IN")}
              </span>
              <span className="text-lg text-muted-foreground line-through">
                ₹{product.originalPrice.toLocaleString("en-IN")}
              </span>
              <span className="text-sm text-green-600 font-semibold bg-green-50 px-2 py-0.5 rounded-full">
                Save ₹
                {(product.originalPrice - product.price).toLocaleString(
                  "en-IN",
                )}
              </span>
            </div>

            {/* Description */}
            <p className="text-muted-foreground text-sm leading-relaxed mb-6">
              {product.description}
            </p>

            {/* Specs */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              {[
                { label: "Purity", value: product.purity },
                { label: "Weight", value: product.weight },
                { label: "Material", value: product.material },
                { label: "Category", value: product.category },
              ].map(({ label, value }) => (
                <div key={label} className="bg-muted/60 rounded-xl px-4 py-3">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-sm font-semibold text-foreground mt-0.5">
                    {value}
                  </p>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex gap-3 mb-6">
              <AppButton
                size="lg"
                fullWidth
                startIcon={
                  addedToCart ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <ShoppingCart className="h-4 w-4" />
                  )
                }
                onClick={handleAddToCart}
                className={addedToCart ? "bg-green-600 hover:bg-green-600" : ""}
              >
                {addedToCart ? "Added!" : "Add to Cart"}
              </AppButton>
              <AppButton
                size="lg"
                fullWidth
                variant="outlined"
                onClick={handleBuyNow}
              >
                Buy Now
              </AppButton>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-3">
              {[
                {
                  icon: Shield,
                  label: "Quality Certified",
                  sub: "Pure silver",
                },
                { icon: Truck, label: "Free Delivery", sub: "On orders ₹999+" },
              ].map(({ icon: Icon, label, sub }) => (
                <div
                  key={label}
                  className="flex flex-col items-center text-center bg-muted/40 rounded-xl p-3 gap-1"
                >
                  <Icon className="h-5 w-5 text-primary mb-1" />
                  <p className="text-xs font-semibold text-foreground">
                    {label}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{sub}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Product Details */}
        <div className="mt-12 border-t border-border pt-10">
          <h2 className="text-xl font-bold text-foreground mb-5">
            Product Details
          </h2>
          <ul className="space-y-3">
            {product.details.map((detail, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="mt-0.5 h-5 w-5 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Check className="h-3 w-3 text-primary" />
                </div>
                <span className="text-sm text-muted-foreground">{detail}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </main>
  );
}
